// @ts-nocheck
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  User, Building2, BarChart3, Users, Calendar, X, ChevronUp,
  Shield, Lock, FileText, Mail, CheckCircle, ChevronDown, Cookie,
  RefreshCw, Settings, LayoutGrid, GitCompare, Layers, Scale, ArrowRightLeft,
  Moon, ArrowLeft
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { supabase } from '../../utils/supabaseClient';
import { useTenantConfig } from '../../contexts';
import logger from '../../utils/logger';
import './AccountOverview.css';

const AccountOverview = ({ userEmail, onTenantSetup, isDarkMode, toggleTheme, onBack }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [expandedSections, setExpandedSections] = useState({
    dataProtection: true,
    authentication: false,
    cookiePolicy: false,
    dataRights: false
  });

  const [quotaData, setQuotaData] = useState(null);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [quotaError, setQuotaError] = useState(false);
  const quotaFetchedRef = useRef(false);

  const [usageData, setUsageData] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState(false);

  const [quotaRequestLoading, setQuotaRequestLoading] = useState(false);
  const [quotaRequestSent, setQuotaRequestSent] = useState(false);
  const [quotaRequestError, setQuotaRequestError] = useState(null);

  const [configsList, setConfigsList] = useState(null);
  const [expandedConfigSection, setExpandedConfigSection] = useState(null);

  const { enabledFeatures } = useTenantConfig();

  const fetchProfile = useCallback(async () => {
    if (!userEmail) {
      setLoading(false);
      return;
    }

    try {
      await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/user/profile/${encodeURIComponent(userEmail)}`,
        {
          credentials: 'include',
          headers: {
            'x-api-key': import.meta.env.VITE_API_KEY,
          }
        }
      );

      if (response.status === 404) {
        setProfile(null);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const data = await response.json();
      setProfile(data);
      
      if (data.has_tenant && data.tenant_name && onTenantSetup) {
        onTenantSetup({
          tenant_id: data.tenant_id,
          tenant_name: data.tenant_name,
          tenant_slug: data.tenant_slug
        });
      }
    } catch (err) {
      if (err.name === 'SessionExpiredError') return;
      logger.error('Error fetching profile:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userEmail, onTenantSetup]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const fetchQuotaData = useCallback(async () => {
    setQuotaLoading(true);
    setQuotaError(false);
    try {
      await supabase.auth.getSession();
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/quota/summary/`,
        {
          credentials: 'include',
          headers: { 'x-api-key': import.meta.env.VITE_API_KEY },
        }
      );
      if (!response.ok) {
        logger.error('Quota summary fetch failed:', response.status, response.statusText);
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setQuotaData(data);
    } catch (err) {
      if (err.name === 'SessionExpiredError') return;
      logger.error('Error fetching quota data:', err);
      setQuotaError(true);
    } finally {
      setQuotaLoading(false);
    }
  }, []);

  const fetchUsageData = useCallback(async () => {
    setUsageLoading(true);
    setUsageError(false);
    try {
      await supabase.auth.getSession();
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/usage/summary/`,
        {
          credentials: 'include',
          headers: { 'x-api-key': import.meta.env.VITE_API_KEY },
        }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setUsageData(await response.json());
    } catch (err) {
      if (err.name === 'SessionExpiredError') return;
      logger.error('Error fetching usage data:', err);
      setUsageError(true);
    } finally {
      setUsageLoading(false);
    }
  }, []);

  const handleRefreshUsage = () => {
    setUsageData(null);
    setQuotaData(null);
    fetchUsageData();
    fetchQuotaData();
  };

  const handleRequestQuota = async () => {
    setQuotaRequestLoading(true);
    setQuotaRequestError(null);
    try {
      const { error } = await supabase.functions.invoke('request-quota', {
        body: {
          userEmail,
          tenantName: profile?.tenant_name || '',
          usage: quotaData?.company?.entity_used ?? 0,
          quota: quotaData?.company?.entity_quota ?? 0,
        },
      });
      if (error) throw error;
      setQuotaRequestSent(true);
    } catch (err) {
      if (err.name === 'SessionExpiredError') return;
      logger.error('Error requesting quota:', err);
      setQuotaRequestError('Failed to send request. Please try again.');
    } finally {
      setQuotaRequestLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'usage' && !quotaFetchedRef.current) {
      quotaFetchedRef.current = true;
      fetchUsageData();
      fetchQuotaData();
    }
  }, [activeTab, fetchUsageData, fetchQuotaData]);

  const fetchConfigsList = useCallback(async () => {
    if (configsList !== null) return;
    try {
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/tenant-config/list/${encodeURIComponent(userEmail)}`,
        { credentials: 'include', headers: { 'x-api-key': import.meta.env.VITE_API_KEY } }
      );
      const data = await response.json();
      setConfigsList(data.configs || []);
    } catch {
      setConfigsList([]);
    }
  }, [userEmail, configsList]);

  const toggleConfigSection = (section) => {
    fetchConfigsList();
    setExpandedConfigSection(prev => prev === section ? null : section);
  };


  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatUsagePeriod = (period) => {
    // "2026-03" → "March 2026"
    const [year, month] = period.split('-');
    return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const FEATURE_ORDER = [
    'mapping', 'merger', 'tb_converter',
    'recon_extract_payroll', 'recon_extract_pl_wages',
    'qoe_flags', 'qoe_gl',
    'comparative',
  ];

  const FEATURE_LABELS = {
    mapping:                'Account Mapper',
    merger:                 'P&L Merger',
    tb_converter:           'Trial Balance Converter',
    recon_extract_payroll:  'Payroll Extraction',
    recon_extract_pl_wages: 'P&L Wages Extraction',
    qoe_flags:              'QoE Analysis',
    qoe_gl:                 'QoE GL Analysis',
    comparative:            'Comparative Analysis',
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

const quotaPct = (used, total) => (total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0);
  const barClass = (pct) => (pct >= 90 ? 'danger' : pct >= 70 ? 'warning' : 'normal');

  const getFeatureDisplayName = (feature) => {
    const names = {
      'mapper': 'P&L Mapper',
      'merger': 'Multi-File Merger',
      'comparative': 'Comparative Analysis',
      'recon': 'Payroll Reconciliation',
      'tb_converter': 'Trial Balance Converter'
    };
    return names[feature] || feature;
  };

  if (loading) {
    return (
      <div className="account-overview-page">
        {onBack && (
          <button type="button" className="account-back-btn" onClick={onBack}>
            <ArrowLeft size={15} />
            <span>Back</span>
          </button>
        )}
        {/* Header skeleton */}
        <div className="account-page-header">
          <div className="account-header-info">
            <div className="skeleton-avatar" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div className="skeleton-line" style={{ width: 160, height: 22 }} />
              <div className="skeleton-line" style={{ width: 220, height: 14 }} />
            </div>
          </div>
        </div>
        {/* Tabs skeleton */}
        <div className="account-tabs" style={{ pointerEvents: 'none' }}>
          {[80, 80, 60, 75].map((w, i) => (
            <div key={i} className="skeleton-line" style={{ width: w, height: 14, margin: '0.875rem 1.25rem', borderRadius: 4 }} />
          ))}
        </div>
        {/* Cards skeleton */}
        <div className="account-section">
          <div className="account-card">
            <div className="account-card-header">
              <div className="skeleton-line" style={{ width: 160, height: 16 }} />
            </div>
            <div className="account-card-body">
              <div className="account-info-grid">
                {[140, 100, 120, 110].map((w, i) => (
                  <div key={i} className="account-info-item">
                    <div className="skeleton-line" style={{ width: 60, height: 10 }} />
                    <div className="skeleton-line" style={{ width: w, height: 16 }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="account-card">
            <div className="account-card-header">
              <div className="skeleton-line" style={{ width: 130, height: 16 }} />
            </div>
            <div className="account-card-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div className="skeleton-line" style={{ height: 14 }} />
                <div className="skeleton-line" style={{ width: '75%', height: 14 }} />
                <div className="skeleton-line" style={{ width: '55%', height: 14 }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="account-overview-page">
      {onBack && (
        <button type="button" className="account-back-btn" onClick={onBack}>
          <ArrowLeft size={15} />
          <span>Back</span>
        </button>
      )}
      {/* Header */}
      <div className="account-page-header">
        <div className="account-header-info">
          <div className="account-avatar-large">
            <User size={28} />
          </div>
          <div>
            <h1 className="account-page-title">Account Settings</h1>
            <p className="account-page-subtitle">{userEmail}</p>
          </div>
        </div>
        {toggleTheme && (
          <label className={`ios-theme-switch ${isDarkMode ? 'is-on' : ''}`}>
            <Moon size={14} className="ios-theme-switch-icon" aria-hidden="true" />
            <span className="ios-theme-switch-label">Dark mode</span>
            <button
              type="button"
              role="switch"
              aria-checked={isDarkMode}
              aria-label="Toggle dark mode"
              onClick={toggleTheme}
              className="ios-theme-switch-track"
            >
              <span className="ios-theme-switch-knob" />
            </button>
          </label>
        )}
      </div>

      {/* Tabs */}
      <div className="account-tabs">
        <button 
          className={`account-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <User size={16} />
          Profile
        </button>
        <button 
          className={`account-tab ${activeTab === 'company' ? 'active' : ''}`}
          onClick={() => setActiveTab('company')}
        >
          <Building2 size={16} />
          Company
        </button>
        <button
          className={`account-tab ${activeTab === 'usage' ? 'active' : ''}`}
          onClick={() => setActiveTab('usage')}
        >
          <BarChart3 size={16} />
          Usage
        </button>
        <button
          className={`account-tab ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          <Shield size={16} />
          Security
        </button>
      </div>

      {/* Tab Content */}
      <div className="account-tab-content">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="account-section">
            <div className="account-card">
              <div className="account-card-header">
                <h3><User size={18} /> Profile Information</h3>
              </div>
              <div className="account-card-body">
                <div className="account-info-grid">
                  <div className="account-info-item">
                    <label>Email</label>
                    <div className="account-info-value">
                      <Mail size={14} />
                      {userEmail}
                    </div>
                  </div>
                  <div className="account-info-item">
                    <label>Role</label>
                    <div className="account-info-value">
                      <span className={`role-badge ${profile?.role || 'user'}`}>
                        {profile?.role || 'User'}
                      </span>
                    </div>
                  </div>
                  <div className="account-info-item">
                    <label>Member Since</label>
                    <div className="account-info-value">
                      <Calendar size={14} />
                      {formatDate(profile?.member_since)}
                    </div>
                  </div>
                  <div className="account-info-item">
                    <label>Authentication</label>
                    <div className="account-info-value">
                      <Lock size={14} />
                      Supabase Auth
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}

        {/* Company Tab */}
        {activeTab === 'company' && (
          <div className="account-section">

            {/* Company Hero */}
            {profile?.has_tenant && (
              <div className="company-hero">
                <div className="company-hero-identity">
                  <div className="company-avatar">
                    {profile.tenant_name[0].toUpperCase()}
                  </div>
                  <div className="company-hero-text">
                    <div className="company-hero-name">{profile.tenant_name}</div>
                    <div className="company-hero-meta">
                      <span className="company-slug-chip">{profile.tenant_slug}</span>
                      <span className="company-hero-dot">·</span>
                      <Users size={13} />
                      <span>{profile.tenant_user_count || 1} member{(profile.tenant_user_count || 1) !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Configurations */}
            {profile?.has_tenant && (
              <div className="account-card">
                <div className="account-card-header">
                  <h3><Settings size={18} /> Configurations</h3>
                </div>
                <div className="account-card-body">
                  <div className="company-config-list">

                    <div className="company-config-row">
                      <button className="company-config-toggle" onClick={() => toggleConfigSection('company')}>
                        <LayoutGrid size={15} className="company-config-row-icon" />
                        <span>Company Configs</span>
                        <span className="company-config-count-badge">{profile.company_config_count || 0}</span>
                        <ChevronDown size={14} className={`company-config-chevron ${expandedConfigSection === 'company' ? 'open' : ''}`} />
                      </button>
                      {expandedConfigSection === 'company' && (
                        <div className="company-config-sub">
                          {configsList === null ? (
                            <div className="company-config-loading">Loading…</div>
                          ) : configsList.filter(c => c.visibility === 'company').length === 0 ? (
                            <div className="company-config-empty">No company configs yet</div>
                          ) : (
                            configsList.filter(c => c.visibility === 'company').map(config => (
                              <div key={config.id} className="company-config-subitem">
                                <FileText size={12} />
                                <span className="company-config-subitem-name">{config.config_name || 'Unnamed Config'}</span>
                                <span className="company-config-type">{config.config_type === 'income_statement' ? 'P&L' : 'BS'}</span>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>

                    <div className="company-config-row last">
                      <button className="company-config-toggle" onClick={() => toggleConfigSection('private')}>
                        <Lock size={15} className="company-config-row-icon" />
                        <span>Private Configs</span>
                        <span className="company-config-count-badge">{profile.private_config_count || 0}</span>
                        <ChevronDown size={14} className={`company-config-chevron ${expandedConfigSection === 'private' ? 'open' : ''}`} />
                      </button>
                      {expandedConfigSection === 'private' && (
                        <div className="company-config-sub">
                          {configsList === null ? (
                            <div className="company-config-loading">Loading…</div>
                          ) : configsList.filter(c => c.visibility === 'private').length === 0 ? (
                            <div className="company-config-empty">No private configs yet</div>
                          ) : (
                            configsList.filter(c => c.visibility === 'private').map(config => (
                              <div key={config.id} className="company-config-subitem">
                                <FileText size={12} />
                                <span className="company-config-subitem-name">{config.config_name || 'Unnamed Config'}</span>
                                <span className="company-config-type">{config.config_type === 'income_statement' ? 'P&L' : 'BS'}</span>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              </div>
            )}

            {/* Active Features */}
            {enabledFeatures && enabledFeatures.length > 0 && (
              <div className="account-card">
                <div className="account-card-header">
                  <h3><CheckCircle size={18} /> Active Features</h3>
                </div>
                <div className="account-card-body">
                  <div className="company-features-list">
                    {enabledFeatures.map(feature => {
                      const featureIcons = {
                        mapper: BarChart3,
                        comparative: GitCompare,
                        merger: Layers,
                        recon: Scale,
                        tb_converter: ArrowRightLeft,
                      };
                      const Icon = featureIcons[feature] || CheckCircle;
                      return (
                        <div key={feature} className="company-feature-row">
                          <Icon size={15} className="company-feature-icon" />
                          <span>{getFeatureDisplayName(feature)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

          </div>
        )}

        {/* Usage Tab */}
        {activeTab === 'usage' && (
          <div className="account-section">
            {/* Monthly Usage */}
            <div className="account-card">
              <div className="account-card-header">
                <h3>
                  <BarChart3 size={18} /> Monthly Usage
                  {usageData?.period && (
                    <span className="quota-billing-period">· {formatUsagePeriod(usageData.period)}</span>
                  )}
                </h3>
                <button
                  className="quota-refresh-btn"
                  onClick={handleRefreshUsage}
                  disabled={usageLoading}
                  title="Refresh usage data"
                >
                  <RefreshCw size={14} className={usageLoading ? 'spinning' : ''} />
                </button>
              </div>
              <div className="account-card-body">
                {usageLoading ? (
                  <div className="quota-activity-list">
                    {[150, 120, 180, 100].map((w, i) => (
                      <div key={i} className="feature-usage-row">
                        <div className="feature-usage-header">
                          <div className="skeleton-line" style={{ width: w, height: 14 }} />
                          <div className="skeleton-line" style={{ width: 48, height: 14 }} />
                        </div>
                        <div className="skeleton-line" style={{ height: 6, borderRadius: 3 }} />
                      </div>
                    ))}
                  </div>
                ) : usageError ? (
                  <div className="quota-error-row">
                    <span>Failed to load usage data.</span>
                    <button className="quota-retry-btn" onClick={handleRefreshUsage}>Retry</button>
                  </div>
                ) : usageData?.features ? (
                  <div className="quota-activity-list">
                    {FEATURE_ORDER
                      .filter(key => key in usageData.features)
                      .map(key => {
                        // Comparative Analysis — expanded entity breakdown
                        if (key === 'comparative') {
                          const compUsed  = quotaData?.company?.comparables_used ?? 0;
                          const erpUsed   = quotaData?.company?.erp_used ?? 0;
                          const totalUsed = quotaData?.company?.entity_used ?? 0;
                          const quota     = quotaData?.company?.entity_quota;
                          const hasQuota  = quota != null;
                          const safeQuota = quota ?? 1;
                          const pieData   = [
                            { name: 'Comparables', value: compUsed },
                            { name: 'ERP Data',    value: erpUsed  },
                          ];
                          const PIE_COLORS = ['#16a34a', '#f59e0b'];

                          return (
                            <div key={key} className="feature-usage-row">
                              <div className="feature-usage-header">
                                <span className="quota-activity-label">Comparative Analysis</span>
                              </div>
                              <div className="comparative-sub-section">
                                {quotaLoading ? (
                                  <>
                                    <div className="quota-skeleton" style={{ height: '1rem', width: '60%' }} />
                                    <div className="quota-skeleton" style={{ height: '1rem', width: '80%', marginTop: '0.5rem' }} />
                                  </>
                                ) : quotaError ? (
                                  <div className="quota-error-row">
                                    <span>Failed to load entity data.</span>
                                    <button className="quota-retry-btn" onClick={handleRefreshUsage}>Retry</button>
                                  </div>
                                ) : quotaData ? (
                                  <>
                                    {/* Pie chart */}
                                    <div className="quota-pie-section">
                                      <span className="quota-pie-title">Entity Distribution</span>
                                      <div className="quota-pie-layout">
                                        <div className="quota-pie-chart">
                                          {(compUsed + erpUsed) > 0 ? (
                                            <ResponsiveContainer width="100%" height={140}>
                                              <PieChart>
                                                <Pie data={pieData} cx="50%" cy="50%"
                                                     innerRadius={36} outerRadius={60}
                                                     paddingAngle={3} dataKey="value" strokeWidth={0}>
                                                  {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                                                </Pie>
                                              </PieChart>
                                            </ResponsiveContainer>
                                          ) : (
                                            <div className="quota-pie-empty">No data</div>
                                          )}
                                        </div>
                                        <div className="quota-pie-legend">
                                          <div className="quota-pie-legend-item">
                                            <span className="quota-pie-dot" style={{ background: PIE_COLORS[0] }} />
                                            <span className="quota-pie-legend-label">Comparables</span>
                                            <span className="quota-pie-legend-value">{compUsed}</span>
                                          </div>
                                          <div className="quota-pie-legend-item">
                                            <span className="quota-pie-dot" style={{ background: PIE_COLORS[1] }} />
                                            <span className="quota-pie-legend-label">ERP Data</span>
                                            <span className="quota-pie-legend-value">{erpUsed}</span>
                                          </div>
                                          <div className="quota-pie-legend-item quota-pie-legend-total">
                                            <span className="quota-pie-legend-label">Total</span>
                                            <span className="quota-pie-legend-value">{compUsed + erpUsed}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Comparables bar */}
                                    <div className="quota-row-header">
                                      <span className="quota-row-label">Comparables</span>
                                      <span className="quota-row-numbers">{compUsed}</span>
                                    </div>
                                    {hasQuota && (
                                      <div className="quota-bar-track">
                                        <div className={`quota-bar-fill ${barClass(quotaPct(compUsed, safeQuota))}`}
                                             style={{ width: `${quotaPct(compUsed, safeQuota)}%` }} />
                                      </div>
                                    )}

                                    {/* ERP Data bar */}
                                    <div className="quota-row-header" style={{ marginTop: '0.5rem' }}>
                                      <span className="quota-row-label">ERP Data</span>
                                      <span className="quota-row-numbers">{erpUsed}</span>
                                    </div>
                                    {hasQuota && (
                                      <div className="quota-bar-track">
                                        <div className={`quota-bar-fill ${barClass(quotaPct(erpUsed, safeQuota))}`}
                                             style={{ width: `${quotaPct(erpUsed, safeQuota)}%` }} />
                                      </div>
                                    )}

                                    {/* Company Total or unlimited label */}
                                    {hasQuota ? (
                                      <div className="quota-row-total comparative-sub-divider">
                                        <div className="quota-row-header">
                                          <span className="quota-row-label">Company Total</span>
                                          <span className="quota-row-numbers">{totalUsed} / {quota}</span>
                                        </div>
                                        <div className="quota-bar-track-total">
                                          <div className={`quota-bar-fill ${barClass(quotaPct(totalUsed, safeQuota))}`}
                                               style={{ width: `${quotaPct(totalUsed, safeQuota)}%` }} />
                                        </div>
                                      </div>
                                    ) : (
                                      <p className="feature-unlimited-label">No entity limit</p>
                                    )}
                                  </>
                                ) : null}
                              </div>
                            </div>
                          );
                        }

                        // Default: simple feature row with progress bar
                        const { used, limit } = usageData.features[key];
                        const isUnlimited = limit === null;
                        const pct = isUnlimited ? 0 : quotaPct(used, limit);
                        return (
                          <div key={key} className="feature-usage-row">
                            <div className="feature-usage-header">
                              <span className="quota-activity-label">{FEATURE_LABELS[key] ?? key}</span>
                              {isUnlimited ? (
                                <span className="feature-usage-count">
                                  {used} <span className="feature-unlimited-badge">∞</span>
                                </span>
                              ) : (
                                <span className="quota-activity-count">{used} / {limit}</span>
                              )}
                            </div>
                            {!isUnlimited && (
                              <div className="quota-bar-track">
                                <div className={`quota-bar-fill ${barClass(pct)}`} style={{ width: `${pct}%` }} />
                              </div>
                            )}
                            {isUnlimited && (
                              <p className="feature-unlimited-label">No monthly limit</p>
                            )}
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <p className="quota-unavailable">No usage data yet</p>
                )}
              </div>
            </div>

            {/* Company Plan */}
            <div className="account-card">
              <div className="account-card-header">
                <h3><Building2 size={18} /> Company Plan</h3>
              </div>
              <div className="account-card-body">
                {quotaLoading ? (
                  <div className="quota-skeleton">
                    <div className="skeleton-line" style={{ marginBottom: '1.25rem', height: 34, borderRadius: 8 }} />
                  </div>
                ) : quotaError ? (
                  <div className="quota-error-row">
                    <span>Failed to load plan data.</span>
                    <button className="quota-retry-btn" onClick={handleRefreshUsage}>Retry</button>
                  </div>
                ) : quotaData ? (
                  <>
                    <div className="quota-plan-row">
                      <span className="quota-plan-badge">{quotaData.usage_plan || 'Starter'} Plan</span>
                      {quotaData.renewal_date && (
                        <span className="quota-renewal">Renews {formatDate(quotaData.renewal_date)}</span>
                      )}
                    </div>
                    <div className="quota-request-footer">
                      {quotaRequestSent ? (
                        <span className="quota-request-success">Request sent — we'll be in touch shortly.</span>
                      ) : (
                        <>
                          {quotaRequestError && (
                            <span className="quota-request-error">{quotaRequestError}</span>
                          )}
                          <button
                            className="quota-request-btn"
                            onClick={handleRequestQuota}
                            disabled={quotaRequestLoading}
                          >
                            {quotaRequestLoading ? 'Sending…' : 'Request Quota Increase'}
                          </button>
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="quota-unavailable">No plan data available</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="account-section">
            {/* Data Protection */}
            <div className="account-card collapsible">
              <button 
                className="account-card-header clickable"
                onClick={() => toggleSection('dataProtection')}
              >
                <h3><Shield size={18} /> How We Protect Your Data</h3>
                {expandedSections.dataProtection ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              {expandedSections.dataProtection && (
                <div className="account-card-body">
                  <div className="security-list">
                    <div className="security-item">
                      <div className="security-icon success">
                        <CheckCircle size={16} />
                      </div>
                      <div className="security-content">
                        <strong>Data Never Stored Permanently</strong>
                        <p>Your Excel files are processed in memory and automatically deleted. We never save your raw financial data.</p>
                      </div>
                    </div>
                    <div className="security-item">
                      <div className="security-icon success">
                        <CheckCircle size={16} />
                      </div>
                      <div className="security-content">
                        <strong>Transient Processing</strong>
                        <p>Files exist only during active processing (typically seconds). Once mapping is complete, all temporary data is wiped.</p>
                      </div>
                    </div>
                    <div className="security-item">
                      <div className="security-icon success">
                        <CheckCircle size={16} />
                      </div>
                      <div className="security-content">
                        <strong>Bank-Grade Encryption</strong>
                        <p>All data in transit is protected with TLS 1.3 encryption. Stored configuration data uses AES-256 encryption at rest.</p>
                      </div>
                    </div>
                    <div className="security-item">
                      <div className="security-icon success">
                        <CheckCircle size={16} />
                      </div>
                      <div className="security-content">
                        <strong>Tenant Isolation</strong>
                        <p>Your company's data is completely isolated from other users. Multi-tenant architecture ensures no data leakage.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Authentication */}
            <div className="account-card collapsible">
              <button 
                className="account-card-header clickable"
                onClick={() => toggleSection('authentication')}
              >
                <h3><Lock size={18} /> Authentication & Access</h3>
                {expandedSections.authentication ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              {expandedSections.authentication && (
                <div className="account-card-body">
                  <div className="security-list">
                    <div className="security-item">
                      <div className="security-icon success">
                        <CheckCircle size={16} />
                      </div>
                      <div className="security-content">
                        <strong>Authentication</strong>
                        <p>Industry-standard authentication powered by Supabase with secure password hashing and email verification.</p>
                      </div>
                    </div>
                    <div className="security-item">
                      <div className="security-icon success">
                        <CheckCircle size={16} />
                      </div>
                      <div className="security-content">
                        <strong>JWT Token Security</strong>
                        <p>Sessions use cryptographically signed JWT tokens with 1-hour expiry. Tokens are automatically refreshed.</p>
                      </div>
                    </div>
                    <div className="security-item">
                      <div className="security-icon success">
                        <CheckCircle size={16} />
                      </div>
                      <div className="security-content">
                        <strong>API Key Protection</strong>
                        <p>All API requests require both authentication and a valid API key, providing defense-in-depth security.</p>
                      </div>
                    </div>
                    <div className="security-item">
                      <div className="security-icon success">
                        <CheckCircle size={16} />
                      </div>
                      <div className="security-content">
                        <strong>Role-Based Access Control</strong>
                        <p>Users can only access their own data. Company admins can manage shared configurations.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Cookie Policy */}
            <div className="account-card collapsible">
              <button 
                className="account-card-header clickable"
                onClick={() => toggleSection('cookiePolicy')}
              >
                <h3><Cookie size={18} /> Cookie Policy</h3>
                {expandedSections.cookiePolicy ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              {expandedSections.cookiePolicy && (
                <div className="account-card-body">
                  <div className="security-list">
                    <div className="security-item">
                      <div className="security-icon success">
                        <CheckCircle size={16} />
                      </div>
                      <div className="security-content">
                        <strong>Essential Session Cookie Only</strong>
                        <p>We use a single session cookie to keep you logged in securely. This cookie is strictly necessary for the application to function and cannot be opted out of.</p>
                      </div>
                    </div>
                    <div className="security-item">
                      <div className="security-icon success">
                        <CheckCircle size={16} />
                      </div>
                      <div className="security-content">
                        <strong>No Tracking or Analytics Cookies</strong>
                        <p>We do not use any third-party tracking, advertising, or analytics cookies. Your browsing activity is never monitored or shared.</p>
                      </div>
                    </div>
                    <div className="security-item">
                      <div className="security-icon success">
                        <CheckCircle size={16} />
                      </div>
                      <div className="security-content">
                        <strong>Server-Side Session Storage</strong>
                        <p>Session data is stored securely on our server using encrypted Redis storage — not in your browser's local storage. The cookie itself contains only an opaque session identifier.</p>
                      </div>
                    </div>
                    <div className="security-item">
                      <div className="security-icon success">
                        <CheckCircle size={16} />
                      </div>
                      <div className="security-content">
                        <strong>Automatic Expiry</strong>
                        <p>Your session cookie expires automatically when you log out or after a period of inactivity. No persistent cookies are stored on your device.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Data Rights */}
            <div className="account-card collapsible">
              <button 
                className="account-card-header clickable"
                onClick={() => toggleSection('dataRights')}
              >
                <h3><FileText size={18} /> Your Data Rights</h3>
                {expandedSections.dataRights ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              {expandedSections.dataRights && (
                <div className="account-card-body">
                  <div className="data-rights-section">
                    <div className="data-stored">
                      <h4>What We Store</h4>
                      <ul>
                        <li><CheckCircle size={12} /> Your email address</li>
                        <li><CheckCircle size={12} /> Company name and settings</li>
                        <li><CheckCircle size={12} /> Chart of Accounts configurations</li>
                        <li><CheckCircle size={12} /> AI-generated mapping guidelines</li>
                      </ul>
                    </div>
                    <div className="data-not-stored">
                      <h4>What We Don't Store</h4>
                      <ul>
                        <li><X size={12} /> Raw Excel files</li>
                        <li><X size={12} /> Account values or balances</li>
                        <li><X size={12} /> Financial transaction data</li>
                        <li><X size={12} /> Client-specific financial records</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        )}
      </div>

      {error && (
        <div className="account-error">
          {error}
          <button className="account-error-dismiss" onClick={() => setError(null)}>×</button>
        </div>
      )}
    </div>
  );
};

export default AccountOverview;
